import * as ToggleGroup from "@radix-ui/react-toggle-group";
import React from "react";
import styled from "styled-components";

export type RadioPanelItem<T extends string = string> = {
  label: string;
  value: T;
};

export interface RadioPanelProps<T extends string> {
  readonly items: RadioPanelItem<T>[];
  readonly value?: T;
  readonly defaultValue?: T;
  readonly label?: string;
  readonly onChange?: (value: T) => void;
}

export function RadioPanel<T extends string>({
  items,
  value,
  defaultValue,
  label,
  onChange,
}: RadioPanelProps<T>) {
  return (
    <Root
      type="single"
      value={value}
      defaultValue={defaultValue}
      aria-label={label}
      onValueChange={(value) => onChange?.(value as T)}
    >
      {items.map((item, index, array) => {
        return (
          <React.Fragment key={item.value}>
            <Item value={item.value} aria-label={item.label}>
              {item.label}
            </Item>
            {index < array.length - 1 && <Seperator />}
          </React.Fragment>
        );
      })}
    </Root>
  );
}

const Seperator = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${({ theme }) => theme.colors.border};
`;

const Root = styled(ToggleGroup.Root)`
  display: flex;
  padding: ${({ theme }) => theme.spacing.spacing1.toString()};
  align-items: center;
  gap: ${({ theme }) => theme.spacing.spacing1.toString()};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderEmphasis};
  background: ${({ theme }) => theme.colors.background};
  width: fit-content;
`;

export const Item = styled(ToggleGroup.Item)`
  background: none;
  display: flex;
  padding: ${({ theme }) => theme.spacing.spacing1.value - 1}px
    ${({ theme }) => theme.spacing.spacing3.value - 1}px;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.spacing1.toString()};
  align-self: stretch;
  border-radius: 3px;
  border: 1px solid transparent;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
  }

  &:focus {
    background: ${({ theme }) => theme.colors.backgroundHover};
    border-color: ${({ theme }) => theme.colors.borderFocus};
    outline: 1px solid ${({ theme }) => theme.colors.borderFocus};
  }

  &[data-state="on"] {
    background: ${({ theme }) => theme.colors.backgroundSelected};
    color: ${({ theme }) => theme.colors.contentSelected};
    font-weight: ${({ theme }) => theme.typography.weightBold};
  }
`;
